import React, { useState, useEffect } from 'react';
import { Camera, Plus, Trash2, Pencil, X, Calendar as CalendarIcon } from 'lucide-react';
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { Calendar } from "../ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "../ui/popover";
import { format } from "date-fns";
import { cn } from "../../lib/utils";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from "../ui/dialog";
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from "../ui/alert-dialog";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "../ui/table";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "../ui/select";
import BarcodeScannerModal from './BarcodeScannerModal';
import { confirmDialog } from "../ui/confirm-dialog";

const Machines = ({ axiosInstance }) => {
    const [isScannerOpen, setIsScannerOpen] = useState(false);
    const [scannedCode, setScannedCode] = useState("");
    const [scannerMode, setScannerMode] = useState(""); // 'main_search', 'add_serial', 'edit_serial'
    
    // State for Stock Machine Management
    const [machines, setMachines] = useState([]);
    const [isAddMachineOpen, setIsAddMachineOpen] = useState(false);
    const [isAddMachineConfirmOpen, setIsAddMachineConfirmOpen] = useState(false);
    const [isEditMachineOpen, setIsEditMachineOpen] = useState(false);
    const [selectedMachine, setSelectedMachine] = useState(null);
    const [newMachines, setNewMachines] = useState([{
        brand: '',
        model: '',
        itemDescription: '',
        itemCode: '',
        serialNumber: '',
        quantity: ''
    }]);
    const [siNumber, setSiNumber] = useState("");
    const [drNumber, setDrNumber] = useState("");
    const [supplier, setSupplier] = useState("");
    const [deliveryDate, setDeliveryDate] = useState(null);

    const [activeRowIndex, setActiveRowIndex] = useState(null);

    // State for Brand Management
    const [brands, setBrands] = useState([]);
    const [isAddBrandOpen, setIsAddBrandOpen] = useState(false);
    const [newBrandName, setNewBrandName] = useState("");

    // State for Machine Details Modal
    const [isMachineDetailsOpen, setIsMachineDetailsOpen] = useState(false);
    const [selectedModel, setSelectedModel] = useState(null);
    const [modelMachines, setModelMachines] = useState([]);
    
    // User info
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const isSuperAdmin = user.email === 'vinsimon23@gmail.com';

    // Filtering & Sorting State
    const [brandFilter, setBrandFilter] = useState("");
    const [searchFilter, setSearchFilter] = useState("");
    const [sortOption, setSortOption] = useState("alphabetical");
    const [mainWarehouseId, setMainWarehouseId] = useState(null);

    // Fetch data on component mount
    useEffect(() => {
        if (axiosInstance) {
            fetchWarehouses();
            fetchMachines();
            fetchBrands();
        }
    }, [axiosInstance]);

    const fetchWarehouses = async () => {
        try {
            const response = await axiosInstance.get('/inventory/warehouses');
            const warehouses = response.data;
            const main = warehouses.find(w => w.name === "Main Warehouse") || warehouses[0];
            if (main) {
                setMainWarehouseId(main.id);
            }
        } catch (error) {
            console.error("Error fetching warehouses:", error);
        }
    };

    const fetchMachines = async () => {
        try {
            const response = await axiosInstance.get('/stock/machines');
            setMachines(response.data);
        } catch (error) {
            console.error("Error fetching machines:", error);
            toast.error("Failed to fetch machines");
        }
    };

    const fetchBrands = async () => {
        try {
            const response = await axiosInstance.get('/stock/brands');
            setBrands(response.data);
        } catch (error) {
            console.error("Error fetching brands:", error);
            toast.error("Failed to fetch brands");
        }
    };

    const handleScanSuccess = (code) => {
        setScannedCode(code);
        
        if (scannerMode === 'main_search') {
            setSearchFilter(code);
        } else if (scannerMode === 'add_serial') {
            if (activeRowIndex !== null) {
                const updatedMachines = [...newMachines];
                updatedMachines[activeRowIndex].serialNumber = code;
                setNewMachines(updatedMachines);
            }
        } else if (scannerMode === 'edit_serial') {
            setSelectedMachine(prev => ({ ...prev, serialNumber: code }));
        }

        setIsScannerOpen(false);
        setScannerMode("");
        setActiveRowIndex(null);
    };

    const openScanner = (mode, index = null) => {
        setScannerMode(mode);
        setActiveRowIndex(index);
        setIsScannerOpen(true);
    };

    const handleMachineInputChange = (index, e) => {
        const { name, value } = e.target;
        const updatedMachines = [...newMachines];
        updatedMachines[index] = {
            ...updatedMachines[index],
            [name]: value
        };
        setNewMachines(updatedMachines);
    };

    const handleAddMachineRow = () => {
        setNewMachines([...newMachines, {
            brand: '',
            model: '',
            itemDescription: '',
            itemCode: '',
            serialNumber: '',
            quantity: ''
        }]);
    };

    const handleRemoveMachineRow = (index) => {
        if (newMachines.length > 1) {
            const updatedMachines = newMachines.filter((_, i) => i !== index);
            setNewMachines(updatedMachines);
        }
    };

    const handleSaveMachine = async () => {
        // Basic validation
        for (const machine of newMachines) {
            if (!machine.brand || !machine.model || !machine.itemCode) {
                toast.error("Please fill in required fields (Brand, Model, Item Code) for all rows");
                return;
            }
        }

        try {
            const payload = newMachines.map(machine => ({
                ...machine,
                quantity: 1,
                si_number: siNumber,
                dr_number: drNumber,
                supplier: supplier,
                delivery_date: deliveryDate
            }));

            await axiosInstance.post('/stock/machines', payload);
            toast.success("Machines added successfully");
            setIsAddMachineOpen(false);
            setNewMachines([{
                brand: '',
                model: '',
                itemDescription: '',
                itemCode: '',
                serialNumber: '',
                quantity: ''
            }]);
            setSiNumber("");
            setDrNumber("");
            setSupplier("");
            setDeliveryDate(null);
            fetchMachines(); // Refresh list
        } catch (error) {
            console.error("Error saving machines:", error);
            if (error.response && error.response.data && error.response.data.detail) {
                toast.error(error.response.data.detail);
            } else {
                toast.error("Failed to save machines");
            }
        }
    };

    const handleRowClick = (aggregatedMachine) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
        // Use filteredMachines instead of raw machines to ensure consistency
        const relatedMachines = filteredMachines.filter(m => 
            normalize(m.model) === normalize(aggregatedMachine.model) && 
            normalize(m.brand) === normalize(aggregatedMachine.brand) &&
            normalize(m.itemDescription) === normalize(aggregatedMachine.itemDescription) &&
            normalize(m.itemCode) === normalize(aggregatedMachine.itemCode)
        );
        setSelectedModel(aggregatedMachine);
        setModelMachines(relatedMachines);
        setIsMachineDetailsOpen(true);
    };

    const handleEditMachine = (machine) => {
        setSelectedMachine(machine);
        setIsEditMachineOpen(true);
        // We keep the details modal open, edit modal will be on top
    };

    const handleEditInputChange = (e) => {
        const { name, value } = e.target;
        setSelectedMachine(prev => ({ ...prev, [name]: value }));
    };

    const handleDeleteMachine = async (machineId) => {
        const confirmed = await confirmDialog({
            title: "Delete Machine",
            description: "Are you sure you want to delete this machine?",
            variant: "destructive"
        });
        if (!confirmed) return;

        try {
            await axiosInstance.delete(`/stock/machines/${machineId}`);
            toast.success("Machine deleted successfully");
            
            // Refresh main list
            await fetchMachines();
            
            // Update modal list
            setModelMachines(prev => prev.filter(m => m.id !== machineId));
            
            // If no machines left for this model, close modal
            if (modelMachines.length <= 1) {
                setIsMachineDetailsOpen(false);
            }
        } catch (error) {
            console.error("Error deleting machine:", error);
            toast.error("Failed to delete machine");
        }
    };

    const handleUpdateMachine = async () => {
        if (!selectedMachine) return;
        
        try {
            const payload = {
                brand: selectedMachine.brand,
                model: selectedMachine.model,
                itemDescription: selectedMachine.itemDescription,
                itemCode: selectedMachine.itemCode,
                serialNumber: selectedMachine.serialNumber,
                quantity: parseInt(selectedMachine.quantity) || 0
            };

            await axiosInstance.put(`/stock/machines/${selectedMachine.id}`, payload);
            toast.success("Machine updated successfully");
            setIsEditMachineOpen(false);
            setSelectedMachine(null);
            
            // Refresh main list
            await fetchMachines();
            
            // Update local state for details modal if it's open
            if (isMachineDetailsOpen && selectedModel) {
                // Re-fetch machines to ensure consistency
                const response = await axiosInstance.get('/stock/machines');
                const allMachines = response.data;
                const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' ');
                const updatedModelMachines = allMachines.filter(m => 
                    normalize(m.model) === normalize(selectedModel.model) && 
                    normalize(m.brand) === normalize(selectedModel.brand) &&
                    normalize(m.itemDescription) === normalize(selectedModel.itemDescription) &&
                    normalize(m.itemCode) === normalize(selectedModel.itemCode) &&
                    (!mainWarehouseId || !m.warehouse_id || m.warehouse_id === mainWarehouseId)
                );
                setModelMachines(updatedModelMachines);
            }
        } catch (error) {
            console.error("Error updating machine:", error);
            toast.error("Failed to update machine");
        }
    };

    const handleSaveBrand = async () => {
        if (!newBrandName.trim()) {
            toast.error("Brand name cannot be empty");
            return;
        }

        try {
            await axiosInstance.post('/stock/brands', { name: newBrandName.trim() });
            toast.success("Brand added successfully");
            setNewBrandName("");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error saving brand:", error);
            if (error.response && error.response.status === 400) {
                toast.error("Brand already exists");
            } else {
                toast.error("Failed to save brand");
            }
        }
    };

    const handleDeleteBrand = async (brandId) => {
        try {
            await axiosInstance.delete(`/stock/brands/${brandId}`);
            toast.success("Brand deleted successfully");
            fetchBrands(); // Refresh list
        } catch (error) {
            console.error("Error deleting brand:", error);
            toast.error("Failed to delete brand");
        }
    };

    // Filter and Sort Machines
    const filteredMachines = machines.filter(machine => {
        const matchesBrand = brandFilter && brandFilter !== "all" ? machine.brand === brandFilter : true;
        const searchLower = searchFilter.toLowerCase();
        const matchesSearch = !searchFilter || 
            (machine.itemCode?.toLowerCase().includes(searchLower) || 
             machine.serialNumber?.toLowerCase().includes(searchLower));
        
        // Filter by last 24 hours (1 day recent added items)
        const oneDayAgo = new Date();
        oneDayAgo.setDate(oneDayAgo.getDate() - 1);
        const matchesDate = machine.createdAt && new Date(machine.createdAt) >= oneDayAgo;

        // Filter by Main Warehouse (exclude transferred items)
        // If mainWarehouseId is not set yet, we might show everything or nothing. 
        // Showing everything is safer to avoid empty list on load.
        // But the goal is to filter.
        const matchesWarehouse = !mainWarehouseId || !machine.warehouse_id || machine.warehouse_id === mainWarehouseId;

        return matchesBrand && matchesSearch && matchesDate && matchesWarehouse;
    });

    // Aggregate Machines by Brand + Model + Description + ItemCode
    const aggregatedMachines = Object.values(filteredMachines.reduce((acc, machine) => {
        const normalize = (str) => (str || '').trim().toLowerCase().replace(/\s+/g, ' '); // Normalize spaces too
        const key = `${normalize(machine.brand)}-${normalize(machine.model)}-${normalize(machine.itemDescription)}-${normalize(machine.itemCode)}`;
        
        if (!acc[key]) {
            acc[key] = {
                ...machine,
                quantity: 0,
                count: 0
            };
        } else {
            // Check for consistency in new fields
            if (acc[key].si_number !== 'Multiple' && acc[key].si_number !== machine.si_number) {
                acc[key].si_number = 'Multiple';
            }
            if (acc[key].dr_number !== 'Multiple' && acc[key].dr_number !== machine.dr_number) {
                acc[key].dr_number = 'Multiple';
            }
            if (acc[key].supplier !== 'Multiple' && acc[key].supplier !== machine.supplier) {
                acc[key].supplier = 'Multiple';
            }
            // Simple date comparison (string or timestamp)
            const date1 = acc[key].delivery_date ? new Date(acc[key].delivery_date).getTime() : 0;
            const date2 = machine.delivery_date ? new Date(machine.delivery_date).getTime() : 0;
            if (acc[key].delivery_date !== 'Multiple' && date1 !== date2) {
                 acc[key].delivery_date = 'Multiple';
            }
        }
        // Assuming 'quantity' field in machine represents available stock for that specific unit (usually 1)
        // If your data model is one entry per physical machine, quantity is 1. 
        // If one entry can represent multiple machines (bulk), we sum it.
        acc[key].quantity += (machine.quantity || 0); 
        acc[key].count += 1;
        return acc;
    }, {}));

    const sortedAggregatedMachines = aggregatedMachines.sort((a, b) => {
        switch (sortOption) {
            case "largest_qty":
                return b.quantity - a.quantity;
            case "lowest_qty":
                return a.quantity - b.quantity;
            case "alphabetical":
            default:
                return a.brand.localeCompare(b.brand) || a.model.localeCompare(b.model);
        }
    });

    return (
        <div className="p-6 space-y-6 h-full flex flex-col">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">Machines Inventory</h2>
            </div>

            {/* Split Layout: 30% Left, 70% Right */}
            <div className="flex-1 grid grid-cols-1 md:grid-cols-[30%_70%] gap-6">
                
                {/* Left Side: Controls */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    {/* Top Left Buttons */}
                    <div className="flex justify-start mb-4 gap-2">
                        <Button onClick={() => setIsAddMachineOpen(true)} className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Machines
                        </Button>
                        <Button onClick={() => setIsAddBrandOpen(true)} variant="outline" className="flex items-center gap-2">
                            <Plus className="h-4 w-4" />
                            Brand
                        </Button>
                    </div>

                    {/* Filtering Section */}
                    <div className="flex flex-col gap-4">
                        <div className="space-y-2">
                            <Label>Brand</Label>
                            <Select value={brandFilter} onValueChange={setBrandFilter}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Select Brand" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="all">All Brands</SelectItem>
                                    {brands.map(brand => (
                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                        </div>

                        <div className="space-y-2">
                            <Label>Search</Label>
                            <div className="flex gap-2">
                                <Input
                                    placeholder="Search Item Code or Serial Number..."
                                    value={searchFilter}
                                    onChange={(e) => setSearchFilter(e.target.value)}
                                    className="flex-1"
                                />
                                <Button 
                                    variant="outline" 
                                    size="icon" 
                                    onClick={() => openScanner('main_search')}
                                >
                                    <Camera className="h-4 w-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Right Side: Stock List Table */}
                <div className="bg-white p-4 rounded-lg shadow flex flex-col h-full">
                    <div className="flex justify-between items-center mb-4">
                        <h3 className="text-lg font-semibold">Recent Activity</h3>
                        <div className="w-[200px]">
                            <Select value={sortOption} onValueChange={setSortOption}>
                                <SelectTrigger>
                                    <SelectValue placeholder="Sort By" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="alphabetical">Alphabetical (A-Z)</SelectItem>
                                    <SelectItem value="largest_qty">Largest Quantity</SelectItem>
                                    <SelectItem value="lowest_qty">Lowest Quantity</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    
                    <div className="border rounded-md flex-1 overflow-auto">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead className="font-bold">Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Delivery Date</TableHead>
                                    {/* Removed Serial Number Column */}
                                    <TableHead>Quantity</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {sortedAggregatedMachines.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={8} className="text-center py-4 text-gray-500">
                                            No machines found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    sortedAggregatedMachines.map((machine, index) => (
                                        <TableRow 
                                            key={`${machine.brand}-${machine.model}-${index}`} 
                                            className="hover:bg-green-100 cursor-pointer transition-colors"
                                            onClick={() => handleRowClick(machine)}
                                        >
                                            <TableCell>{machine.si_number === 'Multiple' ? 'Multiple' : (machine.si_number || '-')}</TableCell>
                                            <TableCell>{machine.dr_number === 'Multiple' ? 'Multiple' : (machine.dr_number || '-')}</TableCell>
                                            <TableCell className="font-bold">{machine.brand}</TableCell>
                                            <TableCell>{machine.model}</TableCell>
                                            <TableCell>{machine.itemDescription}</TableCell>
                                            <TableCell>{machine.itemCode}</TableCell>
                                            <TableCell>{machine.supplier === 'Multiple' ? 'Multiple' : (machine.supplier || '-')}</TableCell>
                                            <TableCell>
                                                {machine.delivery_date === 'Multiple' 
                                                    ? 'Multiple' 
                                                    : (machine.delivery_date ? new Date(machine.delivery_date).toLocaleDateString() : '-')}
                                            </TableCell>
                                            {/* Removed Serial Number Cell */}
                                            <TableCell>{machine.count}</TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>
                </div>
            </div>

            {/* Machine Details Modal */}
            <Dialog open={isMachineDetailsOpen} onOpenChange={setIsMachineDetailsOpen}>
                <DialogContent className="max-w-[85vw] max-h-[80vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>
                            {selectedModel ? `${selectedModel.brand} ${selectedModel.model}` : 'Machine Details'}
                        </DialogTitle>
                        <DialogDescription>
                            Total Quantity: {modelMachines.length}
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="flex-1 overflow-auto mt-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    {isSuperAdmin && <TableHead>Added By</TableHead>}
                                    {isSuperAdmin && <TableHead>Date Created</TableHead>}
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Item Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>SI No.</TableHead>
                                    <TableHead>DR No.</TableHead>
                                    <TableHead>Supplier</TableHead>
                                    <TableHead>Delivery Date</TableHead>
                                    <TableHead>Serial Number</TableHead>
                                    <TableHead className="text-right">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {modelMachines.map((machine) => (
                                    <TableRow key={machine.id}>
                                        {isSuperAdmin && <TableCell>{machine.addedBy || '-'}</TableCell>}
                                        {isSuperAdmin && <TableCell>{new Date(machine.createdAt).toLocaleDateString('en-US', { month: 'long', day: '2-digit', year: 'numeric' })}</TableCell>}
                                        <TableCell>{machine.brand}</TableCell>
                                        <TableCell>{machine.model}</TableCell>
                                        <TableCell>{machine.itemDescription}</TableCell>
                                        <TableCell>{machine.itemCode}</TableCell>
                                        <TableCell>{machine.si_number || '-'}</TableCell>
                                        <TableCell>{machine.dr_number || '-'}</TableCell>
                                        <TableCell>{machine.supplier || '-'}</TableCell>
                                        <TableCell>{machine.delivery_date ? new Date(machine.delivery_date).toLocaleDateString() : '-'}</TableCell>
                                        <TableCell className="font-medium">{machine.serialNumber}</TableCell>
                                        <TableCell className="text-right">
                                            <div className="flex justify-end gap-2">
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleEditMachine(machine)}
                                                >
                                                    <Pencil className="h-4 w-4 text-blue-500" />
                                                </Button>
                                                <Button 
                                                    variant="ghost" 
                                                    size="icon"
                                                    onClick={() => handleDeleteMachine(machine.id)}
                                                >
                                                    <Trash2 className="h-4 w-4 text-red-500" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsMachineDetailsOpen(false)}>Close</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Machine Modal */}
            <Dialog open={isAddMachineOpen} onOpenChange={setIsAddMachineOpen}>
                <DialogContent className="max-w-[90vw] w-full max-h-[90vh] flex flex-col">
                    <DialogHeader>
                        <DialogTitle>Add New Machines</DialogTitle>
                        <DialogDescription>
                            Enter the details of the new machine stock.
                        </DialogDescription>
                    </DialogHeader>
                    
                    <div className="grid grid-cols-3 gap-4 p-4 border-b">
                        <div className="grid gap-2">
                            <Label htmlFor="siNumber">SI Number</Label>
                            <Input
                                id="siNumber"
                                value={siNumber}
                                onChange={(e) => setSiNumber(e.target.value)}
                                placeholder="Enter SI Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="drNumber">DR Number</Label>
                            <Input
                                id="drNumber"
                                value={drNumber}
                                onChange={(e) => setDrNumber(e.target.value)}
                                placeholder="Enter DR Number"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label htmlFor="supplier">Supplier</Label>
                            <Input
                                id="supplier"
                                value={supplier}
                                onChange={(e) => setSupplier(e.target.value)}
                                placeholder="Enter Supplier"
                            />
                        </div>
                        <div className="grid gap-2">
                            <Label>Date of Delivery</Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <Button
                                        variant={"outline"}
                                        className={cn(
                                            "w-full justify-start text-left font-normal",
                                            !deliveryDate && "text-muted-foreground"
                                        )}
                                    >
                                        <CalendarIcon className="mr-2 h-4 w-4" />
                                        {deliveryDate ? format(deliveryDate, "PPP") : <span>Pick a date</span>}
                                    </Button>
                                </PopoverTrigger>
                                <PopoverContent className="w-auto p-0">
                                    <Calendar
                                        mode="single"
                                        selected={deliveryDate}
                                        onSelect={setDeliveryDate}
                                        initialFocus
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>
                    </div>

                    <div className="flex-1 overflow-auto py-4">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead>Brand</TableHead>
                                    <TableHead>Model</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Item Code</TableHead>
                                    <TableHead>Serial No.</TableHead>
                                    <TableHead className="w-[50px]"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {newMachines.map((machine, index) => (
                                    <TableRow key={index}>
                                        <TableCell>
                                            <Select 
                                                value={machine.brand} 
                                                onValueChange={(value) => handleMachineInputChange(index, { target: { name: 'brand', value } })}
                                            >
                                                <SelectTrigger className="w-[150px]">
                                                    <SelectValue placeholder="Brand" />
                                                </SelectTrigger>
                                                <SelectContent>
                                                    {brands.map(brand => (
                                                        <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="model"
                                                value={machine.model}
                                                onChange={(e) => handleMachineInputChange(index, e)}
                                                placeholder="Model"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemDescription"
                                                value={machine.itemDescription}
                                                onChange={(e) => handleMachineInputChange(index, e)}
                                                placeholder="Description"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Input
                                                name="itemCode"
                                                value={machine.itemCode}
                                                onChange={(e) => handleMachineInputChange(index, e)}
                                                placeholder="Item Code"
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <div className="flex gap-2">
                                                <Input
                                                    name="serialNumber"
                                                    value={machine.serialNumber}
                                                    onChange={(e) => handleMachineInputChange(index, e)}
                                                    placeholder="Serial No."
                                                />
                                                <Button 
                                                    type="button"
                                                    variant="outline" 
                                                    size="icon" 
                                                    onClick={() => openScanner('add_serial', index)}
                                                >
                                                    <Camera className="h-4 w-4" />
                                                </Button>
                                            </div>
                                        </TableCell>
                                        <TableCell>
                                            <Button
                                                variant="ghost"
                                                size="icon"
                                                onClick={() => handleRemoveMachineRow(index)}
                                                disabled={newMachines.length === 1}
                                            >
                                                <Trash2 className="h-4 w-4 text-red-500" />
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                        
                        <div className="mt-4 flex justify-end">
                            <Button onClick={handleAddMachineRow} variant="outline" size="sm" className="flex items-center gap-2">
                                <Plus className="h-4 w-4" /> Add Row
                            </Button>
                        </div>
                    </div>

                    <DialogFooter>
                        <Button type="submit" onClick={() => setIsAddMachineConfirmOpen(true)}>Add to Inventory</Button>
                    </DialogFooter>
                    <AlertDialog open={isAddMachineConfirmOpen} onOpenChange={setIsAddMachineConfirmOpen}>
                        <AlertDialogContent>
                            <AlertDialogHeader>
                                <AlertDialogTitle>Add to Inventory?</AlertDialogTitle>
                                <AlertDialogDescription>
                                    Are you sure you want to add these items to inventory?
                                </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction onClick={handleSaveMachine}>Confirm</AlertDialogAction>
                            </AlertDialogFooter>
                        </AlertDialogContent>
                    </AlertDialog>
                </DialogContent>
            </Dialog>

            {/* Edit Machine Modal */}
            <Dialog open={isEditMachineOpen} onOpenChange={setIsEditMachineOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Edit Machine</DialogTitle>
                        <DialogDescription>
                            Update the details of the machine stock.
                        </DialogDescription>
                    </DialogHeader>
                    {selectedMachine && (
                        <div className="grid gap-4 py-4">
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-brand" className="text-right">
                                    Brand
                                </Label>
                                <div className="col-span-3">
                                    <Select 
                                        value={selectedMachine.brand} 
                                        onValueChange={(value) => setSelectedMachine(prev => ({ ...prev, brand: value }))}
                                    >
                                        <SelectTrigger>
                                            <SelectValue placeholder="Select Brand" />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {brands.map(brand => (
                                                <SelectItem key={brand.id} value={brand.name}>{brand.name}</SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-model" className="text-right">
                                    Model
                                </Label>
                                <Input
                                    id="edit-model"
                                    name="model"
                                    value={selectedMachine.model}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemDescription" className="text-right">
                                    Description
                                </Label>
                                <Input
                                    id="edit-itemDescription"
                                    name="itemDescription"
                                    value={selectedMachine.itemDescription}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-itemCode" className="text-right">
                                    Item Code
                                </Label>
                                <Input
                                    id="edit-itemCode"
                                    name="itemCode"
                                    value={selectedMachine.itemCode}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-serialNumber" className="text-right">
                                    Serial No.
                                </Label>
                                <div className="col-span-3 flex gap-2">
                                    <Input
                                        id="edit-serialNumber"
                                        name="serialNumber"
                                        value={selectedMachine.serialNumber}
                                        onChange={handleEditInputChange}
                                        className="flex-1"
                                    />
                                    <Button 
                                        type="button"
                                        variant="outline" 
                                        size="icon" 
                                        onClick={() => openScanner('edit_serial')}
                                    >
                                        <Camera className="h-4 w-4" />
                                    </Button>
                                </div>
                            </div>
                            <div className="grid grid-cols-4 items-center gap-4">
                                <Label htmlFor="edit-quantity" className="text-right">
                                    Quantity
                                </Label>
                                <Input
                                    id="edit-quantity"
                                    name="quantity"
                                    type="number"
                                    value={selectedMachine.quantity}
                                    onChange={handleEditInputChange}
                                    className="col-span-3"
                                />
                            </div>
                        </div>
                    )}
                    <DialogFooter>
                        <Button type="submit" onClick={handleUpdateMachine}>Save Changes</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Add Brand Modal */}
            <Dialog open={isAddBrandOpen} onOpenChange={setIsAddBrandOpen}>
                <DialogContent className="sm:max-w-[425px]">
                    <DialogHeader>
                        <DialogTitle>Add New Brand</DialogTitle>
                        <DialogDescription>
                            Enter a new brand name to add to the list.
                        </DialogDescription>
                    </DialogHeader>
                    <div className="grid gap-4 py-4">
                        <div className="grid grid-cols-4 items-center gap-4">
                            <Label htmlFor="brand-name" className="text-right">
                                Name
                            </Label>
                            <Input
                                id="brand-name"
                                value={newBrandName}
                                onChange={(e) => setNewBrandName(e.target.value)}
                                className="col-span-3"
                                placeholder="Enter brand name..."
                            />
                        </div>
                        
                        <div className="mt-4 border rounded-md p-2 max-h-[200px] overflow-y-auto">
                            <Label className="mb-2 block text-sm font-medium">Brands</Label>
                            {brands.length === 0 ? (
                                <p className="text-sm text-gray-500">No brands found.</p>
                            ) : (
                                <ul className="text-sm space-y-1">
                                    {brands.map((brand) => (
                                        <li key={brand.id} className="flex justify-between items-center text-gray-700 p-1 hover:bg-gray-100 rounded">
                                            <span>• {brand.name}</span>
                                            <Button 
                                                variant="ghost" 
                                                size="icon" 
                                                className="h-6 w-6 text-red-500 hover:text-red-700 hover:bg-red-50"
                                                onClick={() => handleDeleteBrand(brand.id)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                        </div>
                    </div>
                    <DialogFooter>
                        <Button type="submit" onClick={handleSaveBrand}>Save Brand</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            <BarcodeScannerModal 
                isOpen={isScannerOpen} 
                onClose={() => setIsScannerOpen(false)} 
                onScanSuccess={handleScanSuccess} 
            />
        </div>
    );
};

export default Machines;
